<script setup lang="ts">
import { useSignStore } from '@/modules/sign/store';
import { useAppStore } from '@/store';
import { useMyPageStore } from '@/store/myPageStore';
import MyPage from './MyPage.vue';

const MAX_TABS = 10;
const appStore = useAppStore();
const { tabs, current } = storeToRefs(appStore);
const route = useRoute();
const router = useRouter();
const signStore = useSignStore();
const myPageStore = useMyPageStore();
const isOpenMyPageMenu = ref(false);
const myPageModal = ref(false);
const { myInfo, positionList } = storeToRefs(myPageStore);
watch(
  () => route.name,
  () => {
    const newPath = route.fullPath;
    const existing = tabs.value.find((t) => t.name === route.name);
    if (!existing) {
      const matched = router.resolve(newPath).matched;
      const matchedComponent = matched[matched.length - 1]?.components?.default;
      if (!matchedComponent) return;
      if (tabs.value.length >= MAX_TABS) {
        tabs.value.shift();
      }

      if (tabs.value.length === 0 || tabs.value[0].name !== 'Main') {
        tabs.value.unshift({
          fullPath: '/',
          title: 'Main',
          component: matchedComponent,
          name: 'Main',
        });
      }

      if (route.name !== 'Main' && route.name !== '404') {
        tabs.value.push({
          fullPath: newPath,
          title: (route.meta.title as string) || route.name?.toString() || 'Untitled',
          component: matchedComponent,
          name: route.name as string,
        });
      }
    }

    current.value = route.name as string;
  },
  { immediate: true },
);
function goTo(path: string) {
  if (path !== route.name) {
    router.push(path);
  }
}
function closeTab(path: string) {
  const index = tabs.value.findIndex((t) => t.name === path);
  if (index !== -1) {
    tabs.value.splice(index, 1);

    if (current.value === path && tabs.value.length > 1) {
      router.back();
    } else {
      router.push('/');
    }
  }
}
watch(
  () => tabs.value,
  (val) => {
    localStorage.setItem('tabs', JSON.stringify(val));
  },
  { deep: true },
);

watch(
  () => current.value,
  (val) => {
    localStorage.setItem('current', val);
  },
);
onMounted(() => {
  const savedTabs = localStorage.getItem('tabs');
  const savedCurrent = localStorage.getItem('current');

  if (savedTabs) {
    tabs.value = JSON.parse(savedTabs);
  }

  // 실제 라우트와 일치할 때만 current 복원 (404→홈 등 다른 페이지에서 온 경우 현재 라우트 우선)
  if (savedCurrent && savedCurrent === route.name) {
    current.value = savedCurrent;
  } else if (route.name) {
    current.value = route.name as string;
  }
});
function logout() {
  signStore.logout();
}
</script>

<template>
  <v-app-bar
    elevation="0"
    border="bottom"
    color="white"
    class="header position-fixed"
    style="height: 50px"
  >
    <div class="d-flex align-center justify-space-between w-100 header-container">
      <div class="tabs-container">
        <v-tab
          :key="'/'"
          :value="'Main'"
          class="d-flex align-center tab-item home"
          :class="{ selected: current === 'Main' }"
          exact
          :ripple="false"
          @click="goTo('/')"
          style="width: 50px !important; max-width: 50px !important; min-width: 50px !important"
        >
          <v-btn icon @click="goTo('/')" style="min-width: 50px !important">
            <v-icon v-if="current !== 'Main'" size="24">custom:home</v-icon>
            <v-icon v-else size="24">custom:homeFill</v-icon>
          </v-btn>
        </v-tab>
        <v-tabs
          v-model="current"
          grow
          next-icon="custom:historyNext"
          prev-icon="custom:historyPrev"
          show-arrows
        >
          <template v-for="tab in tabs" :key="tab.fullPath">
            <v-tab
              v-if="tab.name !== 'Main'"
              :value="tab.name"
              class="d-flex align-center justify-space-between tab-item"
              @click="goTo(tab.fullPath)"
              :ripple="false"
            >
              <div class="tab-title" :class="{ selected: current === tab.name }">
                {{ tab.title }}
              </div>
              <template #append>
                <v-btn icon="custom:close" @click.stop="closeTab(tab.name)" />
              </template>
            </v-tab>
          </template>
        </v-tabs>
      </div>
      <!-- 로그아웃 디자인과 버튼 -->
      <div class="my-info d-flex align-center ga-2 pr-16 flex-grow-1 justify-end h-100">
        <v-menu :offset="[0, 0]" location="bottom end" v-model="isOpenMyPageMenu">
          <template v-slot:activator="{ props }">
            <v-btn
              v-bind="props"
              class="pa-5"
              style="height: 36px !important; max-height: 36px !important"
            >
              <div class="d-flex flex-column align-start">
                <div style="font-size: 12px; color: var(--body-light)">그레이고</div>

                <div class="d-flex ga-1" style="font-size: 12px">
                  <div class="font-weight-bold">{{ myInfo.name }}</div>

                  <div class="d-flex">
                    <div
                      class="font-weight-bold"
                      v-if="myInfo.positionCode && positionList[myInfo.positionCode]"
                    >
                      {{ positionList[myInfo.positionCode].name }}님
                    </div>

                    <div><v-icon> mdi-chevron-down </v-icon></div>
                  </div>
                </div>
              </div>
            </v-btn>
          </template>
          <v-card class="pa-16">
            <div class="d-flex flex-column ga-2">
              <v-btn variant="elevated" color="primary" @click="myPageModal = true">
                내 정보
              </v-btn>
              <v-btn variant="outlined" color="primary" @click="logout"> 로그아웃 </v-btn>
            </div>
          </v-card>
        </v-menu>
      </div>
    </div>
    <MyPage
      v-if="myPageModal"
      :modelValue="myPageModal"
      @update:modelValue="myPageModal = $event"
    />
  </v-app-bar>
</template>

<style scoped lang="scss">
$size: 16px;
.header::v-deep(.v-toolbar__content) {
  height: $header-height !important;
}
.header-container {
  height: $header-height !important;
}
.tabs-container {
  width: calc(100% - 250px);
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.my-info {
}
.v-tabs,
.v-slide-group {
  --v-tabs-height: $header-height !important;
}
.header {
  &.border-bottom {
    border-bottom-width: 1px;
    border-color: $line-navy !important;
  }
}
.tab-item {
  padding-left: $size !important;
  height: $header-height !important;
  flex: none !important;
  width: auto !important;
  ::v-deep(.v-btn) {
    padding: 0 !important;
    min-width: calc($size * 1.5) !important;
    width: calc($size * 1.5) !important;
    height: calc($size * 1.5) !important;
    max-height: calc($size * 1.5) !important;
  }
  ::v-deep(.v-btn__content) {
    width: 100%;
    justify-content: space-between;
    .tab-title {
      color: $body-light;
      font-weight: 400;
      flex: 1;
      font-size: $size !important;

      &.selected {
        color: $primary;
        font-weight: bold;
      }
    }
  }
  &.home {
    width: 50px;
    min-width: 50px !important;
    padding: 0 !important;
    border-bottom: 2px solid transparent;
    &.selected {
      border-bottom: 2px solid $primary;
    }
  }
}
@media (max-width: $mobile) {
  .tab-item {
    width: 80px;
    min-width: 80px !important;
  }
  .home {
    width: 40px;
    min-width: 40px !important;
  }
}
</style>
